import React, { useMemo, useState } from 'react';
import { IcCheck, IcChevronLeft, IcChevronRight, IcHistory, IcBanknote, IcMoon, IcPhone, IcPlus, IcFlower, IcSun, IcClose, IcTrash, IcUser } from '../../theme/icons';

// Month calendar + day panel for the "Soins réservés" (sessions/rendez-vous).
// scheduled_time arrives from the API as "YYYY-MM-DD HH:MM" (SessionSerializer),
// so day grouping is a plain string slice — no timezone surprises.

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Same convention as the vente-page soin modal: matin → 09:00, après-midi → 14:00.
const PERIOD_TIME = { matin: '09:00', apresmidi: '14:00' };

// Displayed period is derived back from the stored hour (before noon = matin).
// Étiquettes Ki Para : info neutre = gris, l'icône + le libellé font la différence.
const sessionPeriod = (s) => ((s.scheduled_time || '').slice(11, 13) < '12'
  ? { label: 'Matin', Icon: IcSun, cls: 'bdg-neutral' }
  : { label: 'Après-midi', Icon: IcMoon, cls: 'bdg-neutral' });

const pad2 = (n) => String(n).padStart(2, '0');
const dayKey = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
const todayKey = () => {
  const t = new Date();
  return dayKey(t.getFullYear(), t.getMonth(), t.getDate());
};

const EMPTY_BOOKING = {
  service: '', customer: null, customerQuery: '', customerResults: [],
  showDropdown: false, period: '', payment: 'unpaid', notes: '',
};

const SoinsCalendarView = ({ sessions, statusMeta, soinServices, onSearchCustomers, onCreate, onComplete, onCancel, onDelete }) => {
  const now = new Date();
  const [viewMonth, setViewMonth] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [selectedDay, setSelectedDay] = useState(todayKey());
  // "Nouvelle réservation" opens the fields INSIDE the day panel; Retour discards.
  const [booking, setBooking] = useState(false);
  const [form, setForm] = useState(EMPTY_BOOKING);
  const [submitting, setSubmitting] = useState(false);

  // Group sessions by day, each day sorted by hour.
  const byDay = useMemo(() => {
    const map = {};
    (sessions || []).forEach((s) => {
      const key = (s.scheduled_time || '').slice(0, 10);
      if (!key) return;
      (map[key] = map[key] || []).push(s);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time)));
    return map;
  }, [sessions]);

  const { year, month } = viewMonth;
  const monthLabel = new Date(year, month, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Jours du mois précédent en tête de grille (nombre négatif = autre mois,
  // affiché en gris clair, non cliquable — Composants-2 §9).
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(-(daysInPrev - firstWeekday + 1 + i));
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const tKey = todayKey();
  const shiftMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
  };
  const goToday = () => {
    setViewMonth({ year: now.getFullYear(), month: now.getMonth() });
    if (tKey !== selectedDay) closeBooking(); // « Aujourd'hui » change la date → retour auto
    setSelectedDay(tKey);
  };

  const daySessions = byDay[selectedDay] || [];
  const selectedIsPast = selectedDay < tKey;
  const selectedLabel = new Date(`${selectedDay}T00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  // ---- Inline booking form (day panel) ----
  const closeBooking = () => { setBooking(false); setForm(EMPTY_BOOKING); };

  const searchCustomers = async (q) => {
    setForm((f) => ({ ...f, customerQuery: q, customer: null, showDropdown: true, customerResults: q.trim() ? f.customerResults : [] }));
    if (!q.trim()) return;
    try {
      const list = await onSearchCustomers(q);
      setForm((f) => ({ ...f, customerResults: Array.isArray(list) ? list : [] }));
    } catch { /* search errors: keep previous results, non-fatal */ }
  };

  const bookingValid = form.service && form.customer && form.period;

  const submitBooking = async () => {
    if (!bookingValid || submitting) return;
    setSubmitting(true);
    // onCreate (parent) does the API call + succès/erreur feedback; true = créé.
    const ok = await onCreate({
      service: parseInt(form.service, 10),
      customer: form.customer.id,
      scheduled_time: `${selectedDay}T${PERIOD_TIME[form.period]}`,
      status: 'scheduled',
      payment_status: form.payment,
      notes: form.notes.trim(),
    });
    setSubmitting(false);
    if (ok) closeBooking();
  };

  return (
    <div className="kp-split">
      {/* ---- Month grid ---- */}
      <div className="kp-pane bg-white border border-gray-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            {/* Boutons-2 §7 : Précédent / Suivant icône seule = btn-s carré 36px */}
            <button type="button" onClick={() => shiftMonth(-1)} title="Précédent" className="btn-s h-9 w-9 transition-colors"><IcChevronLeft size={13} /></button>
            <button type="button" onClick={() => shiftMonth(1)} title="Suivant" className="btn-s h-9 w-9 transition-colors"><IcChevronRight size={13} /></button>
          </div>
          <span className="text-base font-bold text-gray-800 capitalize">{monthLabel}</span>
          <button type="button" onClick={goToday} className="btn-s px-3 py-1.5 text-xs transition-colors">Aujourd'hui</button>
        </div>

        {/* Calendrier (Brand Kit §05) : en-tête 10.5px gray-400 ; cases
            bordées #E5E7EB rayon 5 padding 4 10.5px min 34px ; jour passé
            fond gray-50 texte gray-400 ; aujourd'hui bordé 2px encre 700 ;
            autre mois gray-300 ; pastilles 8.5px (à venir vertes / passées grises). */}
        {/* Défile dans la carte, sans barre visible — même kit que le
            catalogue de « Nouvelle vente » (.kp-scroll). */}
        <div className="kp-scroll">
        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {WEEKDAYS.map((d) => (
            <div key={d} className="text-center text-[11px] text-gray-400 py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => {
            if (d < 0) {
              return (
                <div key={`e${i}`} className="min-h-[86px] rounded-xl border border-gray-200 p-1.5 text-xs text-gray-300">{-d}</div>
              );
            }
            const key = dayKey(year, month, d);
            const list = byDay[key] || [];
            const isToday = key === tKey;
            const isSelected = key === selectedDay;
            const isPast = key < tKey;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  // Changer de date pendant la saisie = retour automatique :
                  // le formulaire se ferme et rien n'est conservé.
                  if (key !== selectedDay) closeBooking();
                  setSelectedDay(key);
                }}
                className={`min-h-[86px] rounded-xl p-1.5 flex flex-col items-stretch gap-1 text-left transition-all ${
                  isToday
                    ? 'border-2 border-gray-900 font-bold'
                    : isSelected
                      ? 'border border-gray-900'
                      : 'border border-gray-200 hover:border-gray-300'
                } ${isPast ? 'bg-gray-50 text-gray-400' : 'bg-white text-gray-900'}`}
              >
                <span className="self-start text-xs h-5 min-w-[20px] px-1 rounded-full flex items-center justify-center leading-tight">{d}</span>
                {/* Pastilles calendrier (étiquettes Ki Para). Fond = teinte du jour :
                    à venir vert clair, passé gris. Texte = Spruce #15803D dès que le
                    soin est TERMINÉ (owner 2026-09-06), y compris sur un jour passé où
                    la pastille reste grise. Tous les autres états gardent la teinte du
                    jour : un soin annulé reste donc gris, comme demandé. Même règle que
                    le calendrier mobile (chipCls, MobileSoinsCalendar l. 102). */}
                {list.slice(0, 2).map((s) => (
                  <span key={s.id} className={`px-1 py-0.5 rounded-md text-[10px] leading-tight font-normal truncate ${isPast ? 'bg-gray-100' : 'bg-green-100'} ${
                    s.status === 'completed' ? 'text-green-700' : isPast ? 'text-gray-400' : 'text-green-700'
                  }`}>
                    {s.service_name}
                  </span>
                ))}
                {list.length > 2 && (
                  <span className="text-[10px] font-semibold text-gray-400 px-1">+{list.length - 2} autre{list.length - 2 > 1 ? 's' : ''}</span>
                )}
              </button>
            );
          })}
        </div>
        </div>
      </div>

      {/* ---- Day panel ---- */}
      <div className="kp-card kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-gray-800 capitalize">{selectedLabel}</p>
          {daySessions.length > 0 && (
            <span className="bdg bdg-pill justify-center">{daySessions.length}</span>
          )}
        </div>

        {booking && !selectedIsPast ? (
          /* Fields de la nouvelle réservation — écrits directement dans la carte du jour. */
          <div className="kp-scroll p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">Soin <span className="req">*</span></label>
              <select
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="inp w-full px-3 py-2.5 text-sm text-gray-900"
              >
                <option value="">Sélectionner un soin</option>
                {(soinServices || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name} — {(parseFloat(s.price) || 0).toFixed(3)} DT</option>
                ))}
              </select>
              {(soinServices || []).length === 0 && (
                <p className="mt-1 text-xs text-gray-400">Aucun soin disponible dans le catalogue.</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">Client <span className="req">*</span></label>
              <div className="relative">
                <IcUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={12} />
                <input
                  type="text"
                  value={form.customer ? `${form.customer.first_name || ''} ${form.customer.last_name || ''}`.trim() : form.customerQuery}
                  onChange={(e) => searchCustomers(e.target.value)}
                  onFocus={() => setForm((f) => ({ ...f, showDropdown: true }))}
                  placeholder="Rechercher un client..."
                  className="inp w-full pl-9 pr-9 py-2.5 text-sm text-gray-900"
                />
                {form.customer && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, customer: null, customerQuery: '', customerResults: [] }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><IcClose size={12} /></button>
                )}
                {/* Autocomplete client (Brand Kit §05) : liste collée sous le
                    champ, nom en gras + téléphone secondaire, survol vert */}
                {form.showDropdown && !form.customer && form.customerResults.length > 0 && (
                  <div className="ac-list mt-1 rounded-lg max-h-44 overflow-y-auto">
                    {form.customerResults.map((c) => (
                      <div key={c.id} onClick={() => setForm((f) => ({ ...f, customer: c, showDropdown: false }))} className="ac-row px-3 py-2">
                        <span className="ac-name text-sm truncate">{c.first_name} {c.last_name || ''}</span>
                        {c.phone && <span className="ac-sub text-xs shrink-0">{c.phone}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">Période <span className="req">*</span></label>
              <div className="grid grid-cols-2 gap-2">
                {[['matin', 'Matin', IcSun], ['apresmidi', 'Après-midi', IcMoon]].map(([p, label, Icon]) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setForm({ ...form, period: p })}
                    className={`py-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${form.period === p ? 'border-sky-700 bg-white text-sky-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">Paiement</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setForm({ ...form, payment: 'paid' })} className={`py-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${form.payment === 'paid' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  <IcBanknote size={12} /> Payé
                </button>
                <button type="button" onClick={() => setForm({ ...form, payment: 'unpaid' })} className={`py-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${form.payment === 'unpaid' ? 'border-amber-500 bg-white text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  <IcHistory size={12} /> Payer plus tard
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">Note</label>
              <textarea
                rows="2"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Détails du rendez-vous..."
                className="inp w-full px-3 py-2.5 text-sm text-gray-900"
              />
            </div>
          </div>
        ) : daySessions.length === 0 ? (
          /* État vide (Composants-2 §9) : cadre pointillé + icône + phrase ;
             l'action « Ajouter une réservation » vit déjà dans le pied du panneau. */
          <div className="kp-scroll flex items-center justify-center p-4">
          <div className="empty-ki w-full px-4 py-10">
            <IcFlower size={30} className="empty-ki-icon w-[30px] h-[30px] mb-2" />
            <p className="empty-ki-title text-sm mt-0">Aucun soin ce jour</p>
            <p className="empty-ki-sub mb-0">{selectedIsPast ? 'Journée passée.' : 'Réservez un soin pour cette date.'}</p>
          </div>
          </div>
        ) : (
          <div className="kp-scroll divide-y divide-gray-100">
            {daySessions.map((s) => {
              const meta = statusMeta[s.status] || statusMeta.scheduled;
              const period = sessionPeriod(s);
              return (
                <div key={s.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{s.service_name}</p>
                    {Number(s.final_price) > 0 && (
                      <span className="text-sm font-bold text-gray-800 tabular-nums flex-shrink-0">{Number(s.final_price).toFixed(3)} DT</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1.5">
                    <span className={`bdg ${period.cls}`}>
                      <period.Icon size={9} /> {period.label}
                    </span>
                    <span className={`bdg flex-shrink-0 ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                    <p className="text-xs text-gray-600 inline-flex items-center gap-1 min-w-0"><IcUser size={9} className="text-gray-400 flex-shrink-0" /> <span className="truncate">{s.customer_name}</span></p>
                    {s.customer_phone && (
                      <a href={`tel:${s.customer_phone}`} className="text-xs text-gray-600 hover:text-red-600 inline-flex items-center gap-1 tabular-nums transition-colors"><IcPhone size={9} className="text-gray-400" /> {s.customer_phone}</a>
                    )}
                  </div>
                  {s.notes && <p className="text-[11px] text-gray-400 mt-1 italic truncate">“{s.notes}”</p>}
                  <div className="flex items-center gap-1.5 mt-2">
                    {/* Grammaire de la rangée : Supprimer = Destructif bordé (.btn-db),
                        isolé à GAUCHE via mr-auto (qui pousse le reste à droite même
                        quand Annuler/Terminé ne sont pas rendus) · Annuler = .btn-g ·
                        Terminé = Primaire vert (.btn-p), dernier donc au bord droit. */}
                    <button onClick={() => onDelete(s)} title="Supprimer" className="btn-db px-2.5 py-1 text-[11px] gap-1 mr-auto"><IcTrash size={9} /></button>
                    {(s.status === 'scheduled' || s.status === 'in_progress') && (
                      <button onClick={() => onCancel(s)} className="btn-g px-2.5 py-1 text-[11px] gap-1"><IcClose size={9} /> Annuler</button>
                    )}
                    {s.status === 'scheduled' && (
                      <button onClick={() => onComplete(s)} className="btn-p px-2.5 py-1 text-[11px] gap-1"><IcCheck size={9} /> Terminé</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Booking in the past stays blocked — no CTA (ni formulaire) on past days. */}
        {!selectedIsPast && (
          <div className="p-3 border-t border-gray-100">
            {booking ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeBooking}
                  className="btn-g flex-1 py-2.5 text-sm transition-colors"
                >
                  <IcChevronLeft size={10} /> Retour
                </button>
                {/* Réserver conclut la saisie = Primaire vert (.btn-p), comme le même
                    panneau en mobile ; désactivé = opacité 45 % (règle commune du kit). */}
                <button
                  type="button"
                  onClick={submitBooking}
                  disabled={!bookingValid || submitting}
                  className="btn-p flex-1 py-2.5 text-sm transition-colors"
                >
                  <IcCheck size={11} /> {submitting ? 'Réservation...' : 'Réserver'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setBooking(true)}
                className="btn-add-block py-2.5 text-sm gap-2 transition-colors"
              >
                <IcPlus size={11} /> Ajouter une réservation
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SoinsCalendarView;
